/**
 * 내 신청 확인 페이지 (캠페인별)
 *
 * URL: /apply/{mallId}/{shopNo}/my-application/{campaignId}?phone=01012345678
 *
 * 플로우:
 * 1. ?phone= 파라미터로 전화번호 pre-fill
 * 2. phone_session 유효하면 바로 신청 정보 표시
 * 3. phone_session 없으면 SMS 인증 UI
 * 4. 인증 후 신청 정보 카드 렌더링
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import {
  MyApplicationResponse,
  SeedingContact,
  isPhoneLoggedIn,
  clearPhoneSessionToken,
  seedingPublicApi,
} from '@/lib/seedingPublicApi'
import { formatPhoneInput, stripPhone } from '@/lib/phoneUtils'
import { Footer } from '@/features/apply/components/Footer'
import { ContactCard } from '@/features/apply/components/ContactCard'

export const Route = createFileRoute(
  '/apply/$mallId/$shopNo/my-application/$campaignId',
)({
  component: MyApplicationPage,
  validateSearch: (search) => ({
    phone: (search.phone as string) || '',
  }),
})

// 신청 상태 뱃지
const statusConfig: Record<string, { label: string; className: string }> = {
  pending: { label: '대기중', className: 'bg-yellow-100 text-yellow-800' },
  shortlisted: { label: '후보', className: 'bg-primary/10 text-primary' },
  selected: { label: '선정', className: 'bg-green-100 text-green-800' },
  rejected: { label: '미선정', className: 'bg-error/10 text-error' },  // 기존 데이터 호환
  cancelled: { label: '선정 취소', className: 'bg-error/10 text-error' },
}

/** pending + 발표일 이후면 "미선정" 표시 (DB는 pending 유지) */
function getDisplayStatus(application: { status: string; selection_announce_at?: string }) {
  if (
    application.status === 'pending' &&
    application.selection_announce_at &&
    new Date(application.selection_announce_at) <= new Date()
  ) {
    return statusConfig.rejected // "미선정" 표시
  }
  return statusConfig[application.status] || statusConfig.pending
}

// 성별 한글 변환
const genderLabel: Record<string, string> = {
  male: '남성',
  female: '여성',
}

function MyApplicationPage() {
  const { mallId, shopNo, campaignId } = Route.useParams()
  const { phone: phoneFromUrl } = Route.useSearch()

  return (
    <MyApplicationContent
      mallId={mallId}
      shopNo={Number(shopNo)}
      campaignId={campaignId}
      phoneFromUrl={phoneFromUrl}
    />
  )
}

/** 컴포넌트 props (short URL 라우트에서도 재사용) */
export interface MyApplicationContentProps {
  mallId: string
  shopNo: number
  campaignId: string
  phoneFromUrl?: string
  campaignPath?: string // 캠페인 상세 페이지 경로 (short URL용)
}

/** 내 신청 확인 컴포넌트 (라우트 비종속) */
export function MyApplicationContent({
  mallId,
  shopNo,
  campaignId,
  phoneFromUrl = '',
  campaignPath,
}: MyApplicationContentProps) {
  // 인증 상태
  const [phone, setPhone] = useState(formatPhoneInput(phoneFromUrl))
  const [verificationCode, setVerificationCode] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [authError, setAuthError] = useState('')

  // 데이터 상태
  const [application, setApplication] = useState<MyApplicationResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  // 운영자 문의처 — MyApplicationResponse엔 없어서 공개 캠페인 조회로 별도 확보
  const [contact, setContact] = useState<SeedingContact | null>(null)
  const [shopName, setShopName] = useState<string | null>(null)

  // 캠페인 상세 경로 계산
  const detailPath = campaignPath || `/apply/${mallId}/${shopNo}/${campaignId}`

  // 신청 정보 조회
  const fetchApplication = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      setApplication(data)
      setIsAuthenticated(true)

      // 운영자 문의처를 공개 캠페인 조회로 확보 (best-effort, 실패해도 신청 정보 표시엔 영향 없음)
      seedingPublicApi
        .getCampaign(mallId, shopNo, campaignId)
        .then((c) => {
          setContact(c.contact ?? null)
          setShopName(c.shop_name ?? null)
        })
        .catch(() => {
          setContact(null)
          setShopName(null)
        })

      // 브라우저 탭 타이틀 설정 (참여자용 브랜드명)
      document.title = '내 신청 현황 | 체험톡톡'
    } catch (e: unknown) {
      if (e instanceof Error && e.message.includes('401')) {
        // 세션 만료
        clearPhoneSessionToken()
        setIsAuthenticated(false)
        setError(null)
      } else if (e instanceof Error && e.message.includes('404')) {
        setError('해당 캠페인에 대한 신청 정보가 없어요.')
        setIsAuthenticated(true)
      } else {
        setError(e instanceof Error ? e.message : '조회에 실패했어요.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [campaignId, mallId, shopNo])

  // 페이지 진입 시 기존 세션 확인
  useEffect(() => {
    if (isPhoneLoggedIn()) {
      fetchApplication()
    }
  }, [fetchApplication])

  // SMS 인증코드 발송
  const handleSendCode = async () => {
    if (!phone.trim()) return
    setIsSending(true)
    setAuthError('')
    try {
      await seedingPublicApi.sendPhoneCode(stripPhone(phone))
      setIsCodeSent(true)
    } catch (e: unknown) {
      setAuthError(e instanceof Error ? e.message : '인증코드 발송에 실패했어요.')
    } finally {
      setIsSending(false)
    }
  }

  // SMS 인증코드 확인
  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) return
    setIsVerifying(true)
    setAuthError('')
    try {
      await seedingPublicApi.verifyPhoneCode(stripPhone(phone), verificationCode.trim())
      // 인증 성공 → 신청 정보 조회
      await fetchApplication()
    } catch (e: unknown) {
      setAuthError(e instanceof Error ? e.message : '인증에 실패했어요.')
    } finally {
      setIsVerifying(false)
    }
  }

  // 로딩 중
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  // SMS 인증 UI (미인증 상태)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-surface-lowest rounded-2xl shadow-sm p-6 space-y-6">
          <div className="text-center">
            <h1 className="text-xl font-bold text-on-surface">내 신청 확인</h1>
            <p className="text-sm text-on-surface-variant mt-1">신청 시 입력한 전화번호로 인증해주세요</p>
          </div>

          {/* 전화번호 입력 */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-on-surface">전화번호</label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value, phone))}
                placeholder="010-1234-5678"
                className="flex-1 px-4 py-3 border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-primary outline-none"
                disabled={isCodeSent}
              />
              <button
                onClick={handleSendCode}
                disabled={!phone.trim() || isSending}
                className="px-4 py-3 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dim disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSending ? '발송중...' : isCodeSent ? '재발송' : '인증요청'}
              </button>
            </div>
          </div>

          {/* 인증코드 입력 */}
          {isCodeSent && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-on-surface">인증코드</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="6자리 인증코드"
                  maxLength={6}
                  className="flex-1 px-4 py-3 border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-primary outline-none"
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 6 || isVerifying}
                  className="px-4 py-3 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dim disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isVerifying ? '확인중...' : '확인'}
                </button>
              </div>
            </div>
          )}

          {authError && (
            <p className="text-sm text-error text-center">{authError}</p>
          )}

          {/* 캠페인 페이지로 돌아가기 */}
          <div className="text-center pt-2">
            <Link to={detailPath} className="text-sm text-primary hover:underline">
              캠페인 페이지로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // 에러 (인증 완료 후)
  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-xl font-bold text-on-surface mb-2">신청 정보 없음</h1>
          <p className="text-on-surface-variant mb-4">{error}</p>
          <Link to={detailPath} className="text-primary hover:underline text-sm">
            캠페인 페이지로 돌아가기
          </Link>
        </div>
      </div>
    )
  }

  // 신청 정보 없음
  if (!application) return null

  const status = getDisplayStatus(application)

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <div className="flex-1 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* 헤더 */}
        <div className="bg-surface-lowest rounded-2xl shadow-sm p-6">
          <div className="flex items-start gap-4">
            {application.campaign_thumbnail_url && (
              <img
                src={application.campaign_thumbnail_url}
                alt={application.campaign_title}
                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-on-surface leading-tight">
                {application.campaign_title}
              </h1>
              <p className="text-sm text-on-surface-variant mt-1">{application.product_name}</p>
              <div className="mt-2">
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}>
                  {status.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 신청 정보 */}
        <div className="bg-surface-lowest rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-on-surface mb-4">신청 정보</h2>
          <dl className="space-y-3">
            <InfoRow label="이름" value={application.name} />
            {application.email && <InfoRow label="이메일" value={application.email} />}
            {application.gender && (
              <InfoRow label="성별" value={genderLabel[application.gender] || application.gender} />
            )}
            {application.birthdate && <InfoRow label="생년월일" value={application.birthdate} />}
            {(application.zipcode || application.address1) && (
              <InfoRow
                label="배송지"
                value={[
                  application.zipcode && `(${application.zipcode})`,
                  application.address1,
                  application.address2,
                ].filter(Boolean).join(' ')}
              />
            )}
            {application.sns_accounts && application.sns_accounts.length > 0 && (
              <div>
                <dt className="text-sm text-on-surface-variant">SNS</dt>
                <dd className="mt-1 space-y-1">
                  {application.sns_accounts.map((sns, i) => (
                    <div key={i} className="text-sm text-on-surface">
                      <span className="font-medium capitalize">{sns.channel}</span>: {sns.account}
                    </div>
                  ))}
                </dd>
              </div>
            )}
            {application.application_reason && (
              <div>
                <dt className="text-sm text-on-surface-variant">신청 사유</dt>
                <dd className="mt-1 text-sm text-on-surface whitespace-pre-wrap">
                  {application.application_reason}
                </dd>
              </div>
            )}
            <InfoRow
              label="신청일"
              value={new Date(application.created_at).toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            />
          </dl>
        </div>

        {/* 운영자(고객사) 문의처 — 신청 상태/정보 카드 아래에 노출 (미등록이면 자동 숨김) */}
        <ContactCard contact={contact} shopName={shopName} />

        {/* 캠페인 페이지 링크 */}
        <div className="text-center pt-2 pb-4">
          <Link
            to={detailPath}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-surface-lowest border border-border text-on-surface text-sm font-medium rounded-lg hover:bg-surface transition-colors"
          >
            캠페인 페이지 보기
          </Link>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  )
}

/** 정보 행 (label-value) */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex">
      <dt className="text-sm text-on-surface-variant w-20 flex-shrink-0">{label}</dt>
      <dd className="text-sm text-on-surface">{value}</dd>
    </div>
  )
}
